// Grid/EnhancedTableToolbar.js
import React from 'react';
import PropTypes from 'prop-types';
import { Toolbar, Typography, Tooltip, IconButton, alpha } from '@mui/material';
import { Delete, FilterList, Sync } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download'; // Importing the Download icon

/**
 * EnhancedTableToolbar Component.
 *
 * Renders a toolbar with actions such as delete, filter, refresh, and download.
 *
 * @param {Object} props - The component props.
 * @param {number} props.numSelected - The number of selected items.
 * @param {string} props.tableName - The title of the table.
 * @param {Function} props.onDelete - Callback when the delete button is clicked.
 * @param {Function} props.onRefresh - Callback when the refresh button is clicked.
 * @param {Function} props.onToggleFilter - Callback when the filter button is clicked.
 * @param {Function} props.onDownload - Callback when the download button is clicked.
 * @returns {JSX.Element} The EnhancedTableToolbar component.
 */
function EnhancedTableToolbar(props) {
	const {
		numSelected,
		tableName,
		onDelete,
		onRefresh,
		onToggleFilter,
		onDownload
	} = props;

	return (
		<Toolbar
			sx={{
				pl: { sm: 2 },
				pr: { xs: 1, sm: 1 },
				...(numSelected > 0 && {
					bgcolor: (theme) =>
						alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
				}),
			}}
		>
			{numSelected > 0 ? (
				<Typography
					sx={{ flex: '1 1 100%' }}
					color="inherit"
					variant="subtitle1"
					component="div"
				>
					{numSelected} selected
				</Typography>
			) : (
				<Typography
					sx={{ flex: '1 1 100%' }}
					variant="h6"
					id="tableTitle"
					component="div"
				>
					{tableName}
				</Typography>
			)}

			{numSelected > 0 ? (
				<Tooltip title="Delete">
					<IconButton onClick={onDelete}>
						<Delete />
					</IconButton>
				</Tooltip>
			) : (
				<>
					<Tooltip title="Filter list">
						<IconButton onClick={onToggleFilter}>
							<FilterList />
						</IconButton>
					</Tooltip>
					<Tooltip title="Refresh">
						<IconButton onClick={() => {
							console.log("Refreshing data...");
							onRefresh();
						}}>
							<Sync />
						</IconButton>
					</Tooltip>
					{/* Download button triggering the onDownload callback */}
					<Tooltip title="Download">
						<IconButton onClick={onDownload}>
							<DownloadIcon />
						</IconButton>
					</Tooltip>
				</>
			)}
		</Toolbar>
	);
}

EnhancedTableToolbar.propTypes = {
	numSelected: PropTypes.number.isRequired,
	tableName: PropTypes.string.isRequired,
	onDelete: PropTypes.func.isRequired,
	onRefresh: PropTypes.func.isRequired,
	onToggleFilter: PropTypes.func.isRequired,
	onDownload: PropTypes.func.isRequired // New prop for handling download actions
};

export default EnhancedTableToolbar;
